/**
 * =============================================================================
 * Hook: useDealScriptContext
 * Versão: 1.0.0
 *
 * Descrição:
 *   Busca, em uma única chamada RPC (`get_deal_script_context`), todo o
 *   contexto necessário para preencher as variáveis dinâmicas do roteiro
 *   /script de um step de playbook em tempo real para um deal específico.
 *
 *   Retorna estrutura compatível com `ScriptRenderContext` definido em
 *   `src/lib/playbookScript.ts`. Quando ainda não houver dados, devolve
 *   um objeto vazio para que tokens novos apenas fiquem em branco em vez
 *   de quebrar o componente consumidor.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ScriptRenderContext } from '@/lib/playbookScript';

const logger = createLogger('DealScriptContext');

/**
 * Hook que carrega o contexto extra do deal usado pelas variáveis do
 * roteiro/script (produtos, contrato, cobrança, parcelas, etc.).
 */
export function useDealScriptContext(dealId: string | null | undefined) {
  const { currentTenant } = useAuth();

  return useQuery<Partial<ScriptRenderContext>>({
    queryKey: ['deal-script-context', dealId, currentTenant?.id],
    queryFn: async () => {
      if (!dealId || !currentTenant?.id) return {};

      const { data, error } = await supabase.rpc('get_deal_script_context', {
        p_deal_id: dealId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Falha ao carregar contexto do roteiro:', error.message);
        return {};
      }

      // RPC retorna JSONB; convertemos para o tipo do contexto
      return (data ?? {}) as Partial<ScriptRenderContext>;
    },
    enabled: !!dealId && !!currentTenant?.id,
    staleTime: 60 * 1000, // 1 minuto — dados pouco voláteis durante a execução
  });
}
